import type {
  AgentRun,
  AgentSettings,
  AvatarKind,
  Call,
  CallRoutingDecision,
  Conversation,
  ConversationDetail,
  ConversationStatus,
  CsvImportJob,
  KnowledgeDocument,
  KnowledgeSearchHit,
  KnowledgeSource,
  LiveConversation,
  Message,
  Paginated,
  RemoteAccess,
  SessionResponse,
  SupportSettings,
  TicketBoard,
  TicketStage,
  UserAvatarVersion,
  WebhookDelivery,
  WebhookDeliveryDetail,
  WebhookDeliveryStats,
  WebhookIntegration,
  WhatsappSession,
} from '@supporchat/shared';

const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

/**
 * O token de sessão vive apenas em memória.
 *
 * Como o painel roda em um iframe de outro domínio, localStorage e cookies
 * caem nas restrições de armazenamento de terceiros do navegador — além de
 * serem uma superfície desnecessária. A chave original está sempre na URL do
 * iframe, então basta refazer a troca quando a página recarrega.
 */
let sessionToken: string | null = null;

export function setSessionToken(token: string | null): void {
  sessionToken = token;
}

export function getSessionToken(): string | null {
  return sessionToken;
}

async function request<T>(
  path: string,
  options: { method?: string; body?: unknown; formData?: FormData } = {},
): Promise<T> {
  const headers: Record<string, string> = {};

  if (sessionToken) headers.Authorization = `Bearer ${sessionToken}`;
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  const response = await fetch(`${BASE_URL}/api${path}`, {
    method: options.method ?? (options.body || options.formData ? 'POST' : 'GET'),
    headers,
    body: options.formData ?? (options.body === undefined ? undefined : JSON.stringify(options.body)),
  });

  if (!response.ok) {
    throw new ApiError(await readError(response), response.status);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

async function readError(response: Response): Promise<string> {
  try {
    const payload = (await response.json()) as { message?: string | string[] };
    if (Array.isArray(payload.message)) return payload.message.join('; ');
    if (payload.message) return payload.message;
  } catch {
    // resposta sem JSON
  }
  return `Erro ${response.status} ao chamar a API.`;
}

export const api = {
  // ── sessão ──
  createSession: (key: string) => request<SessionResponse>('/auth/session', { body: { key } }),

  // ── conversas ──
  listConversations: (params: { status?: ConversationStatus | 'all'; search?: string; page?: number }) => {
    const query = new URLSearchParams();
    if (params.status) query.set('status', params.status);
    if (params.search) query.set('search', params.search);
    if (params.page) query.set('page', String(params.page));
    return request<Paginated<Conversation>>(`/conversations?${query.toString()}`);
  },
  getConversation: (id: string) => request<ConversationDetail>(`/conversations/${id}`),
  /** Escreve primeiro para um número: confere no WhatsApp, abre o ticket e envia. */
  startConversation: (params: { phone: string; name?: string; content: string }) =>
    request<Conversation>('/conversations/start', { body: params }),
  sendMessage: (id: string, content: string, replyToMessageId?: string) =>
    request<Message>(`/conversations/${id}/messages`, {
      body: replyToMessageId ? { content, replyToMessageId } : { content },
    }),
  /** Troca o texto de uma mensagem já enviada — no painel e no WhatsApp do cliente. */
  editMessage: (conversationId: string, messageId: string, content: string) =>
    request<Message>(`/conversations/${conversationId}/messages/${messageId}`, {
      method: 'PATCH',
      body: { content },
    }),
  /** Apaga para todos uma mensagem enviada pelo painel. */
  deleteMessage: (conversationId: string, messageId: string) =>
    request<Message>(`/conversations/${conversationId}/messages/${messageId}`, { method: 'DELETE' }),
  /** Põe de volta na fila uma mensagem que não chegou ao cliente. */
  resendMessage: (conversationId: string, messageId: string) =>
    request<Message>(`/conversations/${conversationId}/messages/${messageId}/resend`, {
      method: 'POST',
    }),
  markRead: (id: string) => request<Conversation>(`/conversations/${id}/read`, { method: 'POST' }),
  /**
   * Acende (ou apaga) o "digitando..." na conversa do cliente no WhatsApp.
   *
   * Enquanto o atendente escreve, o painel repete a chamada: o servidor mantém o
   * aviso por um prazo curto, para que uma aba fechada no meio da frase não deixe
   * o cliente esperando.
   */
  setTyping: (id: string, typing: boolean) =>
    request<{ ok: true }>(`/conversations/${id}/typing`, { body: { typing } }),
  setHandler: (id: string, handler: 'bot' | 'human') =>
    request<Conversation>(`/conversations/${id}/handler`, { body: { handler } }),
  closeConversation: (id: string) =>
    request<Conversation>(`/conversations/${id}/close`, { method: 'POST' }),
  reopenConversation: (id: string) =>
    request<Conversation>(`/conversations/${id}/reopen`, { method: 'POST' }),
  /**
   * Apaga o ticket, o histórico e os arquivos dele — sem volta.
   *
   * Só funciona para quem está na lista de exclusão do servidor; para os demais
   * a chamada volta 403, mesmo que o botão apareça por um estado antigo da tela.
   */
  deleteTicket: (id: string) =>
    request<{ ticketNumber: number; deletedMessages: number }>(`/conversations/${id}`, {
      method: 'DELETE',
    }),

  /** Mensagem de voz gravada no painel; o servidor converte e envia. */
  sendAudioMessage: (id: string, audio: Blob) => {
    const formData = new FormData();
    formData.append('file', audio, 'gravacao.webm');
    return request<Message>(`/conversations/${id}/audio`, { formData });
  },

  /**
   * Foto, vídeo ou documento para o cliente.
   *
   * A legenda vai no mesmo formulário porque é a mesma mensagem: no WhatsApp ela
   * não é um segundo envio, é o texto que aparece embaixo da foto.
   */
  sendAttachment: (id: string, file: File, caption?: string, replyToMessageId?: string) => {
    const formData = new FormData();
    formData.append('file', file, file.name);
    if (caption?.trim()) formData.append('caption', caption.trim());
    if (replyToMessageId) formData.append('replyToMessageId', replyToMessageId);
    return request<Message>(`/conversations/${id}/attachments`, { formData });
  },
  updateContact: (id: string, changes: { name?: string; phone?: string }) =>
    request<Conversation>(`/conversations/${id}/contact`, { body: changes }),

  // ── tickets (kanban) e monitor ──
  ticketBoard: () => request<TicketBoard>('/conversations/board'),
  moveTicket: (id: string, stage: TicketStage) =>
    request<Conversation>(`/conversations/${id}/stage`, { body: { stage } }),
  liveConversations: (limit?: number) =>
    request<LiveConversation[]>(`/conversations/live${limit ? `?limit=${limit}` : ''}`),

  // ── atendimento humano ──
  getSupportSettings: () => request<SupportSettings>('/support/settings'),
  updateSupportSettings: (values: Partial<SupportSettings>) =>
    request<SupportSettings>('/support/settings', { method: 'PUT', body: values }),

  // ── acesso remoto (RustDesk) ──
  getRemoteAccess: (id: string) => request<RemoteAccess>(`/conversations/${id}/remote-access`),
  saveRemoteAccess: (id: string, changes: { rustdeskId?: string; password?: string; note?: string }) =>
    request<RemoteAccess>(`/conversations/${id}/remote-access`, { body: changes }),
  /**
   * A senha vem em chamada própria, e não junto do resto: assim ela só sai do
   * servidor quando alguém pede, e o pedido fica registrado no log.
   */
  revealRemoteAccessPassword: (id: string) =>
    request<{ password: string }>(`/conversations/${id}/remote-access/reveal`, { method: 'POST' }),

  /**
   * Baixa o arquivo de uma mensagem como URL utilizável em `<img>` e `<video>`.
   *
   * Passa por `fetch` em vez de apontar a tag direto para o endpoint porque o
   * acesso exige o cabeçalho `Authorization` — e a alternativa, token na query
   * string, deixaria credencial em histórico, log de servidor e referer.
   */
  mediaObjectUrl: async (messageId: string): Promise<string> => {
    const response = await fetch(`${BASE_URL}/api/media/${messageId}`, {
      headers: sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {},
    });

    if (!response.ok) {
      const detail = await response.json().catch(() => null);
      throw new ApiError(detail?.message ?? 'Não foi possível carregar o arquivo.', response.status);
    }

    return URL.createObjectURL(await response.blob());
  },

  /**
   * Foto de perfil como URL utilizável em `<img>`.
   *
   * A versão vai na query string: o endereço muda quando a foto muda, então o
   * cache do navegador pode ser longo sem risco de mostrar retrato antigo.
   */
  avatarObjectUrl: async (kind: AvatarKind, id: string, version: string): Promise<string> => {
    const response = await fetch(
      `${BASE_URL}/api/avatars/${kind}/${id}?v=${encodeURIComponent(version)}`,
      { headers: sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {} },
    );

    if (!response.ok) {
      throw new ApiError('Sem foto de perfil.', response.status);
    }

    return URL.createObjectURL(await response.blob());
  },

  /** Versão da foto de cada atendente com foto — para as bolhas das mensagens. */
  userAvatarVersions: () => request<UserAvatarVersion[]>('/avatars/versions'),

  /** Foto do atendente logado. Devolve a versão nova, que o painel usa na URL. */
  uploadMyAvatar: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return request<{ avatarUpdatedAt: string }>('/avatars/me', { formData });
  },
  removeMyAvatar: () => request<void>('/avatars/me', { method: 'DELETE' }),

  // ── whatsapp ──
  listSessions: () => request<WhatsappSession[]>('/whatsapp/sessions'),
  createWhatsappSession: (name: string) => request<WhatsappSession>('/whatsapp/sessions', { body: { name } }),
  connectSession: (id: string) =>
    request<WhatsappSession>(`/whatsapp/sessions/${id}/connect`, { method: 'POST' }),
  disconnectSession: (id: string) =>
    request<{ ok: true }>(`/whatsapp/sessions/${id}/disconnect`, { method: 'POST' }),
  logoutSession: (id: string) =>
    request<{ ok: true }>(`/whatsapp/sessions/${id}/logout`, { method: 'POST' }),

  // ── base de conhecimento ──
  listKnowledge: (params: { source?: KnowledgeSource; search?: string; page?: number }) => {
    const query = new URLSearchParams();
    if (params.source) query.set('source', params.source);
    if (params.search) query.set('search', params.search);
    if (params.page) query.set('page', String(params.page));
    return request<Paginated<KnowledgeDocument>>(`/knowledge?${query.toString()}`);
  },
  knowledgeStats: () =>
    request<{ total: number; ready: number; pending: number; failed: number; bySource: Record<string, number> }>(
      '/knowledge/stats',
    ),
  searchKnowledge: (query: string) =>
    request<KnowledgeSearchHit[]>(`/knowledge/search?q=${encodeURIComponent(query)}`),
  createKnowledge: (input: {
    title: string;
    question?: string;
    answer?: string;
    content?: string;
    category?: string;
    tags?: string[];
  }) => request<KnowledgeDocument>('/knowledge', { body: input }),
  setKnowledgeActive: (id: string, active: boolean) =>
    request<KnowledgeDocument>(`/knowledge/${id}/active`, { body: { active } }),
  deleteKnowledge: (id: string) => request<{ ok: true }>(`/knowledge/${id}`, { method: 'DELETE' }),
  reindexKnowledge: (id: string) => request<{ ok: true }>(`/knowledge/${id}/reindex`, { method: 'POST' }),

  // ── importação de CSV ──
  csvFormat: () =>
    request<{
      recommendedHeader: string[];
      delimiters: string[];
      encoding: string;
      maxSizeMb: number;
      modes: Array<{ name: string; required: string[]; optional: string[] }>;
      notes: string[];
    }>('/knowledge/import/format'),
  previewCsv: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return request<{
      valid: number;
      errors: Array<{ line: number; message: string }>;
      delimiter: string;
      unknownColumns: string[];
      sample: Array<{ title: string; question: string | null; category: string | null }>;
    }>('/knowledge/import/preview', { formData });
  },
  importCsv: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return request<CsvImportJob>('/knowledge/import', { formData });
  },
  listImports: () => request<CsvImportJob[]>('/knowledge/imports'),

  // ── chamadas de voz ──
  callDevices: () => request<{ tokens: string[] }>('/calls/devices'),
  callRouting: (phone: string) =>
    request<CallRoutingDecision>(`/calls/routing?phone=${encodeURIComponent(phone)}`),
  listCalls: () => request<Call[]>('/calls'),
  callsForConversation: (conversationId: string) =>
    request<Call[]>(`/calls/conversation/${conversationId}`),
  dialTarget: (conversationId: string) =>
    request<{ phone: string; name: string | null }>(`/calls/dial/${conversationId}`),
  reportCall: (input: {
    peerPhone: string;
    direction: 'inbound' | 'outbound';
    status: 'ringing' | 'active' | 'ended' | 'missed' | 'rejected' | 'failed';
    providerCallId?: string;
    durationSeconds?: number;
    endReason?: string;
  }) => request<Call>('/calls/report', { body: input }),
  configureCallDevice: (sessionId: string, token: string) =>
    request<{ webhookUrl: string | null }>(`/calls/devices/${sessionId}`, { body: { token } }),

  // ── agente ──
  getAgentSettings: () => request<AgentSettings>('/agent/settings'),
  updateAgentSettings: (values: Partial<AgentSettings>) =>
    request<AgentSettings>('/agent/settings', { method: 'PUT', body: values }),
  agentRuns: (conversationId: string) => request<AgentRun[]>(`/agent/runs/${conversationId}`),
  retryAgent: (conversationId: string) =>
    request<{ ok: true }>(`/agent/runs/${conversationId}/retry`, { method: 'POST' }),

  // ── integração com o sistema existente (webhooks de saída) ──
  webhookConfig: () => request<WebhookIntegration>('/webhooks/config'),
  webhookStats: () => request<WebhookDeliveryStats>('/webhooks/stats'),
  listWebhookDeliveries: (params: {
    status?: string;
    event?: string;
    conversationId?: string;
    page?: number;
    pageSize?: number;
  }) => {
    const query = new URLSearchParams();
    if (params.status) query.set('status', params.status);
    if (params.event) query.set('event', params.event);
    if (params.conversationId) query.set('conversationId', params.conversationId);
    if (params.page) query.set('page', String(params.page));
    if (params.pageSize) query.set('pageSize', String(params.pageSize));
    return request<Paginated<WebhookDelivery>>(`/webhooks/deliveries?${query.toString()}`);
  },
  /** Traz o corpo enviado; por isso é uma chamada à parte da listagem. */
  getWebhookDelivery: (id: string) => request<WebhookDeliveryDetail>(`/webhooks/deliveries/${id}`),
  retryWebhookDelivery: (id: string) =>
    request<WebhookDelivery>(`/webhooks/deliveries/${id}/retry`, { method: 'POST' }),
  retryFailedWebhooks: () =>
    request<{ queued: number }>('/webhooks/deliveries/retry-failed', { method: 'POST' }),
  resyncConversationWebhooks: (conversationId: string) =>
    request<{ queued: number }>(`/webhooks/conversations/${conversationId}/resync`, {
      method: 'POST',
    }),
  testWebhook: () => request<WebhookDelivery>('/webhooks/test', { method: 'POST' }),
};
